import { Button, PasswordField, useTranslation } from '@hbs-us/ui';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { useSession } from '../../auth/useSession.js';
import { apiFetchJson } from '../../lib/apiFetch.js';
import { DeleteMyAccountEntry } from '../erasure/DeleteMyAccountEntry.js';

/**
 * My Account — the signed-in user's own profile + password (UI-SPEC archetype E,
 * settings-style form with a sticky save). The api host EXPOSES the self-write contract
 * (`PATCH /api/v1/account`, 22-06), which is SELF-ONLY: the server resolves the target
 * from the auth principal — one user can never mutate another (SEC-11). The request
 * carries NO user id and NO tenantId.
 *
 * The password change goes through the same `hashPassword` path as the legacy SSR My
 * Account (server-side); the form validates that the new + confirm passwords match
 * client-side as a courtesy, but the server is authoritative. Every color is a locked
 * token (BRAND-06).
 */

const SECTION = 'rounded-md border border-border bg-card p-6';
/** The server's minimum, mirrored from `validatePassword` in the api worker. */
const MIN_PASSWORD_LENGTH = 8;

const FIELD_LABEL = 'font-medium text-foreground text-sm';
const INPUT =
  'mt-1 h-11 w-full rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

interface AccountInput {
  /** Omitted when the name was not edited, so a stale seed can never revert a rename. */
  name?: string;
  newPassword?: string;
  confirmPassword?: string;
}

export function MyAccountRoute() {
  const { t } = useTranslation();
  const { session, refreshSession } = useSession();

  // Seeded from the session: an empty box read as "you have no name", even though the server
  // keeps the current name when this field arrives blank.
  const [name, setName] = useState(session?.user.name ?? '');
  // The value the field started from, and after a save, the value that was saved. The session
  // is re-read after a save, but a screen mounted before that lands would otherwise keep
  // sending the name it was seeded with.
  const [baselineName, setBaselineName] = useState(session?.user.name ?? '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mismatch, setMismatch] = useState(false);

  const mutation = useMutation<{ ok: boolean }, Error, AccountInput>({
    mutationFn: (input) =>
      // SELF-only: no id, no tenantId — the server resolves the principal (SEC-11).
      apiFetchJson<{ ok: boolean }>('/api/v1/account', {
        method: 'PATCH',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: (_data, input) => {
      if (input.name !== undefined) setBaselineName(input.name);
      void refreshSession?.();
    },
  });

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (newPassword || confirmPassword) {
      if (newPassword !== confirmPassword) {
        setMismatch(true);
        return;
      }
    }
    setMismatch(false);
    const trimmedName = name.trim();
    mutation.mutate({
      ...(trimmedName !== baselineName.trim() ? { name: trimmedName } : {}),
      newPassword,
      confirmPassword,
    });
  }

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6">
        <h1 className="font-display font-semibold text-3xl text-foreground">
          {t('users:account.title')}
        </h1>
        <p className="mt-1 max-w-2xl text-muted-foreground text-sm">
          {t('users:account.subtitle')}
        </p>
      </header>

      <form className="max-w-2xl space-y-4" onSubmit={onSubmit}>
        <section className={SECTION}>
          <h2 className="mb-4 font-display font-semibold text-foreground text-lg">
            {t('users:account.profileHeading')}
          </h2>
          <div className="grid gap-4">
            <div>
              <label htmlFor="account-name" className={FIELD_LABEL}>
                {t('users:account.name')}
              </label>
              <input
                id="account-name"
                className={INPUT}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          </div>
        </section>

        <section className={SECTION}>
          <h2 className="mb-4 font-display font-semibold text-foreground text-lg">
            {t('users:account.passwordHeading')}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {/*
              Both rules printed here are the server's, not this form's guesses:
              PATCH /api/v1/account runs `validatePassword` (at least 8 characters,
              qelm-backend/workers/api/src/lib/validation.ts) and rejects a new password
              that does not equal the confirmation. Saying so up front is cheaper than a
              round trip that rejects a password already chosen.
            */}
            <PasswordField
              className="bg-background"
              label={t('users:account.newPassword')}
              placeholder={t('common:password.newPlaceholder')}
              helperText={t('common:password.minLength', { count: MIN_PASSWORD_LENGTH })}
              showLabel={t('common:password.show')}
              hideLabel={t('common:password.hide')}
              value={newPassword}
              autoComplete="new-password"
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <PasswordField
              className="bg-background"
              label={t('users:account.confirmPassword')}
              placeholder={t('common:password.confirmPlaceholder')}
              helperText={t('common:password.mustMatch')}
              showLabel={t('common:password.show')}
              hideLabel={t('common:password.hide')}
              value={confirmPassword}
              autoComplete="new-password"
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
          {mismatch ? (
            <p role="alert" className="mt-2 text-destructive text-sm">
              {t('users:account.passwordMismatch')}
            </p>
          ) : null}
        </section>

        {mutation.isError ? (
          <p role="alert" className="text-destructive text-sm">
            {t('users:account.error')}
          </p>
        ) : null}
        {mutation.isSuccess ? (
          <p role="status" className="text-secondary-foreground text-sm">
            {t('users:account.success')}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" disabled={mutation.isPending}>
            {t('users:account.save')}
          </Button>
        </div>
      </form>
      <div className="mt-6 max-w-2xl">
        <DeleteMyAccountEntry />
      </div>
    </div>
  );
}
